import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals'

class PerformanceMonitor {
  constructor() {
    this.metrics = {}
    this.observers = []
    this.init()
  }

  init() {
    // Track Core Web Vitals
    this.trackCoreWebVitals()
    
    // Track custom metrics
    this.trackCustomMetrics()
    
    // Setup resource monitoring
    this.setupResourceMonitoring()
  }

  trackCoreWebVitals() {
    // Cumulative Layout Shift
    getCLS((metric) => {
      this.reportMetric('CLS', metric)
    })

    // First Input Delay
    getFID((metric) => {
      this.reportMetric('FID', metric)
    })

    // First Contentful Paint
    getFCP((metric) => {
      this.reportMetric('FCP', metric)
    })

    // Largest Contentful Paint
    getLCP((metric) => {
      this.reportMetric('LCP', metric)
    })

    // Time to First Byte
    getTTFB((metric) => {
      this.reportMetric('TTFB', metric)
    })
  }

  trackCustomMetrics() {
    // Track Vue app initialization time
    performance.mark('vue-init-start')
    
    window.addEventListener('DOMContentLoaded', () => {
      performance.mark('vue-init-end')
      const vueInitTime = performance.measure('vue-init-time', 'vue-init-start', 'vue-init-end')
      this.reportMetric('Vue Init Time', { value: vueInitTime.duration })
    })

    // Track route changes
    this.trackRouteChanges()
  }

  trackRouteChanges() {
    let routeStartTime = performance.now()
    
    if (window.app?.$router) {
      window.app.$router.beforeEach((to, from, next) => {
        routeStartTime = performance.now()
        next()
      })

      window.app.$router.afterEach((to) => {
        const routeLoadTime = performance.now() - routeStartTime
        this.reportMetric('Route Change Time', { 
          value: routeLoadTime,
          route: to.name 
        })
      })
    }
  }

  setupResourceMonitoring() {
    // Monitor image loading performance
    const imageObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType === 'img') {
          this.reportMetric('Image Load Time', {
            value: entry.responseEnd - entry.startTime,
            resource: entry.name
          })
        }
      })
    })

    imageObserver.observe({ entryTypes: ['resource'] })
    this.observers.push(imageObserver)

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          this.reportMetric('Long Task', {
            value: entry.duration,
            startTime: entry.startTime
          })
        })
      })

      try {
        longTaskObserver.observe({ entryTypes: ['longtask'] })
        this.observers.push(longTaskObserver)
      } catch (e) {
        // Long task API not supported
      }
    }
  }

  reportMetric(name, metric) {
    this.metrics[name] = metric
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`${name}:`, metric)
    }

    // Send to analytics service (implement based on your analytics provider)
    this.sendToAnalytics(name, metric)
  }

  sendToAnalytics(name, metric) {
    // Example: Send to Google Analytics
    if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'web_vitals', {
        event_category: 'Performance',
        event_label: name,
        value: Math.round(metric.value),
        custom_map: {
          metric_id: metric.id,
          metric_value: metric.value,
          metric_delta: metric.delta
        }
      })
    }

    // Example: Send to custom analytics endpoint
    if (window.fetch && process.env.VUE_APP_ANALYTICS_ENDPOINT) {
      fetch(process.env.VUE_APP_ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          metric: name,
          value: metric.value,
          timestamp: Date.now(),
          page: window.location.pathname,
          userAgent: navigator.userAgent
        })
      }).catch(err => {
        console.warn('Failed to send analytics:', err)
      })
    }
  }

  // Get current metrics
  getMetrics() {
    return this.metrics
  }

  // Clean up observers
  disconnect() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor()

export default performanceMonitor

// Performance optimization utilities
export const performanceOptimizations = {
  // Preload critical resources
  preloadCriticalResources() {
    const resources = [
      { href: '/assets/logo.png', as: 'image' },
      { href: 'https://fonts.googleapis.com/css2?family=Tajawal:wght@200;300;400;500;700;800;900&display=swap', as: 'style' },
      { href: '/assets/main.css', as: 'style' }
    ]
    
    resources.forEach(resource => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.href = resource.href
      link.as = resource.as
      if (resource.as === 'style') {
        link.onload = function() { this.rel = 'stylesheet' }
      }
      document.head.appendChild(link)
    })
  },

  // Lazy load images with intersection observer
  lazyLoadImages() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target
            img.src = img.dataset.src
            img.classList.remove('lazy')
            observer.unobserve(img)
          }
        })
      })

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img)
      })
    }
  },

  // Optimize images with WebP support
  checkWebPSupport() {
    return new Promise(resolve => {
      const webP = new Image()
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2)
      }
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA'
    })
  },

  // Minimize layout shifts
  addImageDimensions() {
    document.querySelectorAll('img:not([width]):not([height])').forEach(img => {
      // Add aspect ratio to prevent layout shift
      img.style.aspectRatio = '16/9' // Default, adjust as needed
    })
  },

  // Enable service worker
  registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => {
            console.log('SW registered: ', registration)
          })
          .catch(registrationError => {
            console.log('SW registration failed: ', registrationError)
          })
      })
    }
  },

  // DNS prefetch for external domains
  addDNSPrefetch() {
    const domains = [
      'https://fonts.googleapis.com',
      'https://cdnjs.cloudflare.com',
      'https://erpnext.com',
      'https://images.unsplash.com'
    ]
    
    domains.forEach(domain => {
      const link = document.createElement('link')
      link.rel = 'dns-prefetch'
      link.href = domain
      document.head.appendChild(link)
    })
  },

  // Optimize critical rendering path
  optimizeCriticalPath() {
    // Inline critical CSS (should be done at build time)
    const criticalCSS = `
      .hero-section { 
        background: linear-gradient(135deg, #0f4640 0%, #1a635b 100%);
        min-height: 60vh;
      }
      .nav-bar {
        background: white;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      }
    `
    
    const style = document.createElement('style')
    style.textContent = criticalCSS
    document.head.appendChild(style)
  },

  // Initialize all performance optimizations
  init() {
    this.preloadCriticalResources()
    this.addDNSPrefetch()
    this.lazyLoadImages()
    this.addImageDimensions()
    this.registerServiceWorker()
    
    // Run after DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.optimizeCriticalPath()
      })
    } else {
      this.optimizeCriticalPath()
    }
  }
}

// SEO Performance metrics
export const seoMetrics = {
  // Core Web Vitals tracking
  trackCoreWebVitals() {
    // Track Largest Contentful Paint (LCP)
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        console.log('LCP:', entry.startTime)
        // Send to analytics
      }
    }).observe({entryTypes: ['largest-contentful-paint']})

    // Track First Input Delay (FID)
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        console.log('FID:', entry.processingStart - entry.startTime)
        // Send to analytics
      }
    }).observe({entryTypes: ['first-input']})

    // Track Cumulative Layout Shift (CLS)
    let clsValue = 0
    let clsEntries = []
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value
          clsEntries.push(entry)
        }
      }
      console.log('CLS:', clsValue)
    }).observe({entryTypes: ['layout-shift']})
  },

  // Track page load times
  trackPageLoad() {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0]
      const loadTime = navigation.loadEventStart - navigation.fetchStart
      console.log('Page Load Time:', loadTime)
      // Send to analytics
    })
  }
} 